import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const fns = () => getFunctions(undefined, 'asia-south1');

/** Null when Razorpay would not tell us. Never rendered as zero — see below. */
export interface BalanceView {
  available: boolean;
  balanceMinor: number | null;
  refundCreditsMinor: number | null;
  feeCreditsMinor: number | null;
  error: string | null;
}

export interface QueuedRefund {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  amountMinor: number;
  reason: string;
  status: 'WAITING_FUNDS' | 'FAILED';
  attempts: number;
  requestedAt: number;
  lastAttemptAt: number | null;
  lastError: string | null;
  /** Null when the balance is unknown, which is not the same as "no". */
  coveredByBalance: boolean | null;
}

export interface WebhookEventView {
  id: string;
  event: string;
  subject: string;
  status: string;
  attempts: number;
  receivedAt: number;
  error: string | null;
}

export interface PaymentOps {
  balance: BalanceView;
  queued: QueuedRefund[];
  note: string;
  disputes: Record<string, unknown>[];
  recentEvents: WebhookEventView[];
}

export async function getPaymentOps(): Promise<PaymentOps> {
  const call = httpsCallable<Record<string, never>, PaymentOps>(fns(), 'getPaymentOps');
  return (await call({})).data;
}

export async function resolvePendingRefund(payload: {
  orderId: string;
  action: 'retry' | 'cancel';
  note: string;
}) {
  return httpsCallable(fns(), 'resolvePendingRefund')(payload);
}

/** One row of the money ledger for an order: captures, failures and refunds. */
export interface PaymentRow {
  id: string;
  kind: 'PAYMENT' | 'REFUND';
  status: string;
  amountMinor: number;
  feeMinor: number | null;
  netMinor: number | null;
  method: string | null;
  refundStatus: string | null;
  reason: string | null;
  errorDescription: string | null;
  settlementId: string | null;
  createdAt: number;
}

/**
 * Every payment record for one order, newest first.
 *
 * Read straight from Firestore rather than through a callable: the rules
 * already allow an admin to read `payments`, and there is nothing to compute.
 */
export async function paymentsForOrder(orderId: string): Promise<PaymentRow[]> {
  const snap = await getDocs(query(
    collection(db, 'payments'),
    where('orderId', '==', orderId),
    orderBy('createdAt', 'desc'),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PaymentRow, 'id'>) }));
}
